import { ChangeDetectionStrategy, Component, computed, ElementRef, input, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';

/**
 * Single log line. Permissive shape so it can wrap both the generated {@code Log} DTO and
 * the raw payload of the live MQTT stream.
 */
export interface LogLine {
  /** ISO timestamp or ms since epoch — caller's choice. Used for the gutter. */
  timestamp?: string | number;
  /** Free-form level. Common values: TRACE, DEBUG, INFO, WARN, ERROR, FATAL. */
  level?: string;
  /** The actual log text. */
  message: string;
}

/**
 * Live log viewer with pause/resume, level filter, and substring search. Designed for the
 * integrator detail pages — caller pushes lines into the {@code lines} input as they arrive
 * (via WS or polling). The component handles auto-scroll, filtering and rendering.
 *
 * <p>Pause means "freeze the displayed list and stop auto-scrolling" — incoming lines are still
 * captured upstream, but rendering doesn't update until resumed. This is the standard pattern
 * for a debug log viewer.
 */
@Component({
  selector: 'lib-log-stream',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  template: `
    <div class="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white">
      <header class="flex items-center justify-between gap-3 border-b border-gray-200 px-3 py-2">
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="rounded px-2 py-1 text-xs font-medium"
            [class.bg-primary-500]="!paused()"
            [class.bg-gray-200]="paused()"
            [class.text-white]="!paused()"
            [class.text-gray-700]="paused()"
            (click)="togglePause()">
            {{ paused() ? 'Resume' : 'Pause' }}
          </button>
          <span class="text-xs text-gray-500"> {{ filteredLines().length }} / {{ lines().length }} lines </span>
        </div>
        <div class="flex items-center gap-2">
          <select
            class="rounded border border-gray-300 px-2 py-1 text-xs"
            [(ngModel)]="levelFilter"
            (ngModelChange)="onLevelChange()">
            <option value="">All levels</option>
            <option value="ERROR">ERROR</option>
            <option value="WARN">WARN</option>
            <option value="INFO">INFO</option>
            <option value="DEBUG">DEBUG</option>
          </select>
          <input
            type="search"
            class="rounded border border-gray-300 px-2 py-1 text-xs"
            placeholder="Search…"
            [(ngModel)]="search"
            (ngModelChange)="onSearchChange()" />
        </div>
      </header>
      <pre
        #scroller
        class="m-0 max-h-96 overflow-y-auto bg-gray-50 px-3 py-2 font-mono text-xs leading-relaxed">@for (line of filteredLines(); track $index) {<span [class]="lineClasses(line)">{{ formatLine(line) }}</span>
}</pre>
    </div>
  `,
})
export class LogStreamComponent {
  /**
   * Caller-controlled list. Append-only is the normal pattern; we don't try to diff. The
   * component re-renders the latest snapshot on every signal change.
   */
  lines = input.required<LogLine[]>();

  protected readonly paused = signal(false);
  protected readonly levelFilter = signal('');
  protected readonly search = signal('');

  protected readonly snapshotWhenPaused = signal<LogLine[] | null>(null);

  protected readonly filteredLines = computed(() => {
    const source = this.paused() ? (this.snapshotWhenPaused() ?? this.lines()) : this.lines();
    const level = this.levelFilter();
    const q = this.search().toLowerCase();
    return source.filter(l => {
      if (level && l.level !== level) return false;
      if (q && !l.message.toLowerCase().includes(q)) return false;
      return true;
    });
  });

  protected scroller = viewChild<ElementRef<HTMLPreElement>>('scroller');

  protected togglePause(): void {
    if (!this.paused()) {
      this.snapshotWhenPaused.set([...this.lines()]);
      this.paused.set(true);
    } else {
      this.snapshotWhenPaused.set(null);
      this.paused.set(false);
      this.scrollToBottom();
    }
  }

  protected onLevelChange(): void {
    // Signal already wired via ngModel; explicit handler kept for future side-effects.
    this.scrollToBottom();
  }

  protected onSearchChange(): void {
    this.scrollToBottom();
  }

  private scrollToBottom(): void {
    queueMicrotask(() => {
      const el = this.scroller()?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  protected formatLine(line: LogLine): string {
    const ts = line.timestamp ? `[${formatTimestamp(line.timestamp)}] ` : '';
    const lvl = line.level ? `${line.level.padEnd(5)} ` : '';
    return `${ts}${lvl}${line.message}`;
  }

  protected lineClasses(line: LogLine): string {
    switch (line.level) {
      case 'ERROR':
      case 'FATAL':
        return 'block text-red-600';
      case 'WARN':
        return 'block text-warmYellow-700';
      case 'INFO':
        return 'block text-gray-700';
      case 'DEBUG':
      case 'TRACE':
        return 'block text-gray-500';
      default:
        return 'block text-gray-600';
    }
  }
}

function formatTimestamp(ts: string | number): string {
  const d = typeof ts === 'number' ? new Date(ts) : new Date(ts);
  if (isNaN(d.getTime())) return String(ts);
  return d.toISOString().slice(11, 19);
}
